import { useState, useEffect } from 'react';
import { Deployments, Organization, Homeless } from 'types';
import SamaritanFetch from '../../../../../api/httpClient';

export enum ProfileStage {
    LIVE = 'LIVE',
    EXITED = 'EXITED',
    PENDING = 'PENDING',
    ELIGIBILITY_LIST = 'ELIGIBILITY_LIST',
    TARGET_LIST = 'TARGET_LIST',
    TEMPLATE = 'TEMPLATE',
    NEEDS_REVIEW = 'NEEDS_REVIEW',
    NEVER_ENGAGED = 'NEVER_ENGAGED',
    UNSUCCESSFUL_PENDING = 'UNSUCCESSFUL_PENDING',
    TARGET_NOT_INTERESTED = 'TARGET_NOT_INTERESTED',
    NO_LONGER_ELIGIBLE = 'NO_LONGER_ELIGIBLE'
}

const profileStageLabels: Record<ProfileStage, string> = {
    [ProfileStage.LIVE]: 'Live',
    [ProfileStage.EXITED]: 'Exited',
    [ProfileStage.PENDING]: 'Pending',
    [ProfileStage.UNSUCCESSFUL_PENDING]: 'Unsuccessful Pending',
    [ProfileStage.ELIGIBILITY_LIST]: 'Eligibility List',
    [ProfileStage.TARGET_LIST]: 'Target List',
    [ProfileStage.NEEDS_REVIEW]: 'Needs Review',
    [ProfileStage.NEVER_ENGAGED]: 'Never Engaged',
    [ProfileStage.TARGET_NOT_INTERESTED]: 'Target Not Interested',
    [ProfileStage.NO_LONGER_ELIGIBLE]: 'No Longer Eligible',
    [ProfileStage.TEMPLATE]: 'Template'
};

export interface ProfileStageOption {
    value: ProfileStage;
    label: string;
}

export const useAdminMemberInfoFormOptions = (homeless: Homeless) => {
    const [deployments, setDeployments] = useState<Deployments[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const profileStageOptions = Object.values(ProfileStage).map(stage => ({
        value: stage,
        label: profileStageLabels[stage]
    }));

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const [deploymentsRes, orgsRes] = await Promise.all([
                    SamaritanFetch.v3.get('/deployments'),
                    SamaritanFetch.v3.get('/organizations')
                ]);
                
                if (!deploymentsRes.ok || !orgsRes.ok) {
                    throw new Error('Failed to fetch member form options');
                }

                const [deploymentsData, orgsData] = await Promise.all([
                    deploymentsRes.json(),
                    orgsRes.json()
                ]);

                // Handle different possible response structures like in admin hooks
                const deploymentsArray = Array.isArray(deploymentsData) ? deploymentsData : deploymentsData.deployments || [];
                const orgsArray = Array.isArray(orgsData) ? orgsData : orgsData.organizations || [];

                setDeployments(deploymentsArray);
                setOrganizations(orgsArray);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch member form options');
                console.error('Error fetching member form options:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [homeless.id]); // Only re-fetch when member ID changes

    return {
        deployments,
        organizations,
        profileStageOptions,
        isLoading,
        error
    };
}; 